/**
 * The active organization (00-contracts §12.2).
 *
 * It is a tiny external store rather than a React context so that `api/client.ts` — a plain
 * module, not a component — can read it on every request without a hook. `ActiveOrgProvider`
 * subscribes the React tree to the same store, so there is exactly one value, not two that
 * drift.
 */
import type { MeResponse } from '@tvitvi/shared';
import { useSyncExternalStore } from 'react';

import { getGuestOrganizationId, subscribeGuestOrganization } from './guest-org';

const STORAGE_KEY = 'tvitvi.activeOrganizationId';

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode, blocked storage, or a non-browser test environment. Not fatal: the API
    // falls back to users.last_active_organization_id, which is the authority anyway.
    return null;
  }
}

let current: string | null = typeof window === 'undefined' ? null : readStored();
const listeners = new Set<() => void>();

/**
 * GJESTEMODUS VINNER (sak 232, T5): står fanen som gjest i en organisasjon, er det den headeren
 * navngir. Personens egen organisasjon ligger urørt under og kommer tilbake når gjestemodus slutter.
 */
export function getActiveOrganizationId(): string | null {
  return getGuestOrganizationId() ?? current;
}

/**
 * HJEMMET FOR DET PERSONLIGE — varslene (P10, funn C).
 *
 * Et varsel hører til personen i hens EGEN organisasjon (sak 216: «varsel i egen organisasjon»). I
 * gjestemodus navngir headeren organisasjonen som delte, og der svarte klokka 403
 * (`notification:list` er ikke i gjestens hviteliste) — og hadde den svart, ville den vist en annen
 * organisasjons varsler. Hjemmet er den lagrede organisasjonen gjestemodus aldri skriver over, når den
 * er et medlemskap; ellers det personlige huset, eller det første medlemskapet. Utenfor gjestemodus
 * er det den aktive organisasjonen, som før.
 */
export function homeOrganizationIdFor(me: MeResponse): string | null {
  if (me.activeOrgRole !== 'guest') return me.activeOrganizationId;
  const houses = me.memberships;
  if (current !== null && houses.some((one) => one.organizationId === current)) return current;
  return (
    houses.find((one) => one.organizationKind === 'personal')?.organizationId ??
    houses[0]?.organizationId ??
    null
  );
}

export function setActiveOrganizationId(organizationId: string | null): void {
  if (current === organizationId) return;
  current = organizationId;
  try {
    if (organizationId === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, organizationId);
  } catch {
    // See readStored(): losing the local hint is harmless.
  }
  for (const listener of listeners) listener();
}

export function subscribeActiveOrganization(listener: () => void): () => void {
  listeners.add(listener);
  const unsubscribeGuest = subscribeGuestOrganization(listener);
  return () => {
    listeners.delete(listener);
    unsubscribeGuest();
  };
}

export function useActiveOrganizationId(): string | null {
  return useSyncExternalStore(subscribeActiveOrganization, getActiveOrganizationId, () => null);
}
